import React, { useCallback, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { functions } from "../firebase/firebaseConfig";
import { useCart } from "../cart/CartContext";

// Publishable key is safe to expose in frontend code.
const stripePromise = loadStripe("pk_live_51QTQ9vAV749Cj0YB39Q5uNlPm8Mnw5aW7PiMOgRgMMh0YjZxJDnqSCWDFkv4CaNxjPqGEVjIo7N7Q0bppWnRkfvk00q4dHj2YO");

// Guest checkout is allowed — Stripe's own Checkout form collects the
// buyer's email automatically for the receipt, so no sign-in is required
// just to buy something from the store.
export default function Checkout() {
  const { items } = useCart();
  // Discount code: typed here, or saved by the educator promo popup
  // ("Use this code"). The server checks it (functions/cartCheckout.js).
  const [code, setCode] = useState(() => {
    try {
      return localStorage.getItem("sbl-promo-code") || "";
    } catch {
      return "";
    }
  });
  const [typed, setTyped] = useState(code);
  const [codeErr, setCodeErr] = useState("");

  const fetchClientSecret = useCallback(async () => {
    const createCartCheckoutSession = httpsCallable(functions, "createCartCheckoutSession");
    const result = await createCartCheckoutSession({
      // Price and quantity are what Stripe charges on; the rest is
      // personalization written to the order record. Prices are still never
      // trusted from the client — only price_... IDs are sent.
      items: items.map((i) => ({
        priceId: i.stripePriceRef,
        quantity: i.quantity,
        productId: i.productId,
        title: i.title,
        size: i.size,
        colorKey: i.colorKey,
        colorLabel: i.colorLabel,
        schoolId: i.schoolId || null,
        schoolName: i.schoolName || null,
        schoolState: i.schoolState || null,
        schoolLogoUrl: i.schoolLogoUrl || null,
        needsArtwork: i.needsArtwork === true,
      })),
      returnUrl: `${window.location.origin}/order-confirmation`,
      promoCode: code || undefined,
    }).catch((e) => {
      // a bad code shouldn't block buying — say so and check out without it
      if (code && String(e?.message || "").includes("code")) {
        setCodeErr(e.message);
        setCode("");
        try {
          localStorage.removeItem("sbl-promo-code");
        } catch {
          /* ignore */
        }
      }
      throw e;
    });
    return result.data.clientSecret;
  }, [items, code]);

  if (items.length === 0) {
    return (
      <div style={{ padding: "80px 20px", textAlign: "center" }}>
        <h2>Your cart is empty</h2>
        <a href="/products">Browse products</a>
      </div>
    );
  }

  return (
    <div style={{ padding: "40px 0" }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setCodeErr("");
          setCode(typed.trim().toUpperCase());
        }}
        style={{ display: "flex", gap: 8, justifyContent: "center", alignItems: "center", flexWrap: "wrap", margin: "0 auto 18px", maxWidth: 560, fontFamily: "inherit" }}
      >
        <label htmlFor="sbl-code" style={{ fontWeight: 700 }}>
          Discount code
        </label>
        <input id="sbl-code" value={typed} onChange={(e) => setTyped(e.target.value.toUpperCase())} placeholder="e.g. TEACH50" style={{ padding: "8px 12px", borderRadius: 10, border: "2px solid #d6ccf5", textTransform: "uppercase" }} />
        <button type="submit" style={{ padding: "8px 16px", borderRadius: 10, border: 0, background: "#6a00ff", color: "#fff", fontWeight: 800, cursor: "pointer" }}>
          Apply
        </button>
        {code && !codeErr && <span style={{ color: "#15803d", fontWeight: 800 }}>✓ {code} applied</span>}
        {codeErr && <span style={{ color: "#b91c1c", fontWeight: 700, flexBasis: "100%", textAlign: "center" }}>{codeErr}</span>}
      </form>
      <EmbeddedCheckoutProvider key={code || "none"} stripe={stripePromise} options={{ fetchClientSecret }}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
}
